import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Check,
  Filter,
  Loader2,
  Search,
  Tag,
  User2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { type PagedResult } from '@/services/base/base.service';
import {
  feedbackService,
  type FeedbackFilters,
  type FeedbackItem,
  type FeedbackStatus,
  type UpdateFeedbackRequest,
} from '@/services/feedback.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const statusOptions: { label: string; value: FeedbackStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'New', value: 'New' },
  { label: 'In Review', value: 'InReview' },
  { label: 'Action Planned', value: 'ActionPlanned' },
  { label: 'Resolved', value: 'Resolved' },
  { label: 'Closed', value: 'Closed' },
  { label: 'Rejected', value: 'Rejected' },
];

const statusColors: Record<FeedbackStatus, string> = {
  New: 'bg-destructive/10 text-destructive',
  InReview:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
  ActionPlanned: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200',
  Resolved:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
  Closed: 'bg-muted text-muted-foreground',
  Rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200',
};

function StatusBadge({ status }: { status: FeedbackStatus }) {
  return (
    <Badge className={cn('capitalize', statusColors[status])}>{status}</Badge>
  );
}

function FeedbackRow({
  item,
  onSelect,
}: {
  item: FeedbackItem;
  onSelect: (item: FeedbackItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="w-full rounded-xl border bg-card p-4 text-left shadow-sm transition hover:border-primary"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StatusBadge status={item.status} />
            <span>{item.category}</span>
            {item.contextType && <span>• {item.contextType}</span>}
            {item.userId && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <User2 className="h-3.5 w-3.5" />
                {item.userId.slice(0, 8)}...
              </span>
            )}
          </div>
          <p className="line-clamp-2 text-sm text-foreground">{item.message}</p>
          {item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
              {item.tags.map((tag) => (
                <Badge variant="outline" key={tag} className="bg-muted/50">
                  <Tag className="mr-1 h-3 w-3" />
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 text-right text-[11px] text-muted-foreground">
          <div>
            {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
          </div>
          {item.rating ? (
            <div className="font-semibold text-destructive">
              ⭐ {item.rating}/5
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function FeedbackDetailSheet({
  item,
  open,
  onOpenChange,
  onUpdate,
  isUpdating,
}: {
  item: FeedbackItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onUpdate: (payload: UpdateFeedbackRequest) => void;
  isUpdating: boolean;
}) {
  const [status, setStatus] = useState<FeedbackStatus | undefined>(
    item?.status,
  );
  const [response, setResponse] = useState(item?.response ?? '');
  const [assignedTo, setAssignedTo] = useState(item?.assignedTo ?? '');
  const [tagsInput, setTagsInput] = useState(item?.tags?.join(',') ?? '');

  // Reset local state when item changes
  useMemo(() => {
    setStatus(item?.status);
    setResponse(item?.response ?? '');
    setAssignedTo(item?.assignedTo ?? '');
    setTagsInput(item?.tags?.join(',') ?? '');
  }, [item]);

  const parsedTags = tagsInput
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>Feedback detail</SheetTitle>
        </SheetHeader>
        {!item ? (
          <div className="mt-6 text-sm text-muted-foreground">
            Select a feedback item to inspect.
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <StatusBadge status={item.status} />
              <span>{item.category}</span>
              {item.contextType && <span>• {item.contextType}</span>}
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <User2 className="h-3.5 w-3.5" />
                {item.userId.slice(0, 8)}...
              </span>
            </div>
            <p className="text-sm text-foreground">{item.message}</p>
            {item.response && (
              <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                <div className="text-[11px] font-semibold uppercase text-muted-foreground">
                  Latest response
                </div>
                <p>{item.response}</p>
              </div>
            )}
            <Separator />
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as FeedbackStatus)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions
                    .filter((o) => o.value !== 'all')
                    .map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Response</Label>
              <Textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Reply to the user"
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Assignee</Label>
                <Input
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  placeholder="Owner / team"
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="priority, ux, bug"
                />
              </div>
            </div>
            <Button
              className="mt-2 inline-flex items-center gap-2"
              onClick={() =>
                onUpdate({ status, response, assignedTo, tags: parsedTags })
              }
              disabled={isUpdating}
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save changes
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function FeedbackAdminPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filters, setFilters] = useState<FeedbackFilters>({
    sort: 'newest',
    maxResultCount: 20,
    skipCount: 0,
  });

  const { data, isLoading, isFetching } = useQuery<PagedResult<FeedbackItem>>({
    queryKey: ['admin-feedback', filters],
    queryFn: () => feedbackService.getList(filters),
    placeholderData: (previousData) => previousData,
  });

  const mutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateFeedbackRequest;
    }) => feedbackService.update(id, payload),
    onSuccess: (item) => {
      setSelected(item);
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
    },
  });

  const handleSelect = (item: FeedbackItem) => {
    setSelected(item);
    setSheetOpen(true);
  };

  const categories = useMemo(() => {
    if (!data?.items) return [];
    return Array.from(new Set(data.items.map((i) => i.category))).sort();
  }, [data?.items]);

  const statusValue = filters.status ?? 'all';
  const handleStatusChange = (value: string) =>
    setFilters((prev) => ({
      ...prev,
      status: value === 'all' ? undefined : (value as FeedbackStatus),
      skipCount: 0,
    }));

  const handleCategoryChange = (value: string) =>
    setFilters((prev) => ({
      ...prev,
      category: value === 'all' ? undefined : value,
      skipCount: 0,
    }));

  const handleSearchChange = (value: string) =>
    setFilters((prev) => ({
      ...prev,
      search: value || undefined,
      skipCount: 0,
    }));

  const handleSortChange = (value: string) =>
    setFilters((prev) => ({
      ...prev,
      sort: value as 'newest' | 'oldest',
      skipCount: 0,
    }));

  const loadMore = () => {
    setFilters((prev) => ({
      ...prev,
      maxResultCount: (prev.maxResultCount ?? 20) + 20,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Feedback</h1>
          <p className="text-sm text-muted-foreground">
            Review and respond to user feedback from the engagement app.
          </p>
        </div>
        {isFetching && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      <Card className="border-muted">
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Filters</CardTitle>
            <CardDescription>
              Filter by status, category, user, or search text.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={statusValue} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem value={option.value} key={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.category ?? 'all'}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {categories.map((category) => (
                  <SelectItem value={category} key={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ToggleGroup
              type="single"
              value={filters.sort}
              onValueChange={handleSortChange}
            >
              <ToggleGroupItem value="newest" aria-label="Newest">
                <Filter className="mr-1 h-4 w-4" /> Newest
              </ToggleGroupItem>
              <ToggleGroupItem value="oldest" aria-label="Oldest">
                <Filter className="mr-1 h-4 w-4" /> Oldest
              </ToggleGroupItem>
            </ToggleGroup>
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                className="w-[200px]"
                placeholder="Search message/response"
                defaultValue={filters.search ?? ''}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading feedback...
            </div>
          ) : data && data.items.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {data.items.map((item) => (
                <FeedbackRow
                  key={item.id}
                  item={item}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" /> No feedback found. Adjust
              filters.
            </div>
          )}
          {data && data.totalCount > (data.items?.length ?? 0) && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={isFetching}
              >
                Load more
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <FeedbackDetailSheet
        item={selected}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onUpdate={(payload) =>
          selected && mutation.mutate({ id: selected.id, payload })
        }
        isUpdating={mutation.isPending}
      />
    </div>
  );
}
